import type { ValueOf } from 'app/globalTypes';
import {
    type ColumnInput,
    type Combine,
    type InputDataset,
    type SqlColumn,
    TypeFamily
} from 'app/lib/types/server';
import { ALL_KEYWORDS } from 'app/lib/codemirror/grammars/snowsql/keywords.js';

export const OPS = {
    EQUALS: 'equals',
    NOT_EQUALS: 'not equals',
    NOT_EQUALS_OR_EMPTY: 'not equals or empty',
    STARTS_WITH: 'starts with',
    NOT_STARTS_WITH: 'does not start with',
    CONTAINS: 'contains',
    NOT_CONTAINS: 'does not contain',
    ENDS_WITH: 'ends with',
    NOT_ENDS_WITH: 'does not end with',
    IS_EMPTY: 'is empty',
    IS_NOT_EMPTY: 'is not empty',
    IS_NULL: 'is null',
    IS_NOT_NULL: 'is not null',
    EQ: '=',
    NOT_EQ: '!=',
    LT: '<',
    LTE: '<=',
    GT: '>',
    GTE: '>=',
    BEFORE: 'before',
    NOT_BEFORE: 'not before',
    AFTER: 'after',
    NOT_AFTER: 'not after',
    BETWEEN: 'between',
    NOT_BETWEEN: 'not between',
    IN: 'in',
    NOT_IN: 'not in',
    ARRAYS_OVERLAP: 'arrays overlap'
};

const CastFunction = {
    [TypeFamily.STRING]: 'TO_VARCHAR',
    [TypeFamily.INTEGER]: 'TO_NUMBER',
    DECIMAL: 'TO_DECIMAL',
    [TypeFamily.DOUBLE]: 'TO_DOUBLE',
    [TypeFamily.BOOLEAN]: 'TO_BOOLEAN',
    [TypeFamily.DATE]: 'TO_DATE',
    [TypeFamily.TIME]: 'TO_TIME',
    [TypeFamily.TIMESTAMP]: 'TO_TIMESTAMP',
    [TypeFamily.VARIANT]: 'TO_VARIANT',
    [TypeFamily.ARRAY]: 'TO_ARRAY',
    [TypeFamily.OBJECT]: 'TO_OBJECT',
    [TypeFamily.GEOGRAPHY]: 'TO_GEOGRAPHY'
};

export const TryCastFunction = {
    [TypeFamily.STRING]: 'TO_VARCHAR',
    [TypeFamily.INTEGER]: 'TRY_TO_NUMBER',
    [TypeFamily.DOUBLE]: 'TRY_TO_DOUBLE',
    [TypeFamily.BOOLEAN]: 'TRY_TO_BOOLEAN',
    [TypeFamily.DATE]: 'TRY_TO_DATE',
    [TypeFamily.TIMESTAMP]: 'TRY_TO_TIMESTAMP',
    [TypeFamily.TIME]: 'TO_TIME',
    [TypeFamily.VARIANT]: 'TO_VARIANT',
    [TypeFamily.ARRAY]: 'TO_ARRAY',
    [TypeFamily.OBJECT]: '',
    [TypeFamily.GEOGRAPHY]: 'TO_GEOGRAPHY'
};

export const normalizeType = (type: string) => {
    switch (type) {
        case 'VARCHAR':
            return TypeFamily.STRING;
        case 'DECIMAL':
            return TypeFamily.DOUBLE;
        default:
            return type;
    }
};

export const NUMERIC_TYPES = [TypeFamily.INTEGER, TypeFamily.DOUBLE, 'DECIMAL'];

const IDENTIFIER_TEST_REGEX = /^[_A-Z][_A-Z\d$]*$/;
// collect additional patterns which are not covered by regular keywords
const ADDITIONAL_KEYWORDS_TEST_REGEX = /^e2/;

export function processExtractedColumns(
    extractedColumns: {
        path: string;
        type: TypeFamily & 'DECIMAL';
        expand: boolean;
    }[],
    getExtractedColumnName: (path: string) => string
) {
    const outputs: {
        name: string;
        type: TypeFamily;
    }[] = [];
    const extracts: {
        path: string;
        as: ValueOf<typeof TryCastFunction>;
        expand: boolean;
    }[] = [];

    extractedColumns.forEach(({ path, type, expand }) => {
        let modifiedPath = path;

        outputs.push({ name: getExtractedColumnName(path), type });

        if (
            [
                TypeFamily.INTEGER,
                TypeFamily.BOOLEAN,
                TypeFamily.DOUBLE,
                'DECIMAL',
                TypeFamily.TIMESTAMP,
                TypeFamily.DATE
            ].includes(type)
        ) {
            modifiedPath = path + '::TEXT';
        }

        extracts.push({ path: modifiedPath, as: TryCastFunction[type], expand });
    });

    return { outputs, extracts };
}

export const needsQuotes = (identifier: string) =>
    (!IDENTIFIER_TEST_REGEX.test(identifier) && identifier !== '*') ||
    ADDITIONAL_KEYWORDS_TEST_REGEX.test(identifier.toLowerCase()) ||
    ALL_KEYWORDS.includes(identifier.toLowerCase());

export const quoted = (identifier: string) => `"${identifier.replaceAll('"', '""')}"`;

export const quoteIfNeeded = (identifier: string) =>
    needsQuotes(identifier) ? quoted(identifier) : identifier;

export const isNumeric = (type: TypeFamily) => NUMERIC_TYPES.includes(type);

export const isDate = (type: TypeFamily) => [TypeFamily.DATE, TypeFamily.TIMESTAMP].includes(type);

export const isBoolean = (type: TypeFamily) => type === TypeFamily.BOOLEAN;

export const hasRhs = (op: ValueOf<typeof OPS>) =>
    ![OPS.IS_EMPTY, OPS.IS_NOT_EMPTY, OPS.IS_NULL, OPS.IS_NOT_NULL].includes(op);

interface InputColumnWithName extends ColumnInput {
    name: string;
    value: string;
}

const getInputColumnsListForUnion = (
    outputColumns: SqlColumn[],
    inputColumns: InputColumnWithName[]
) =>
    inputColumns
        .map(({ name, value }, i) => {
            const columnName = name ? quoteIfNeeded(name) : value ? `'${value}'` : 'NULL';
            const outputColumn = outputColumns[i]!;

            const cast = outputColumn.type.toUpperCase() as keyof typeof CastFunction;
            return `${CastFunction[cast]}(${columnName}) AS ${quoteIfNeeded(outputColumn.name)}`;
        })
        .join(', ');

export const getSelectStmtForUnionInputColumns = (
    outputColumns: SqlColumn[],
    inputColumns: InputColumnWithName[],
    datasetName: string
) =>
    `SELECT ${getInputColumnsListForUnion(outputColumns, inputColumns)} FROM ${quoteIfNeeded(
        datasetName
    )}`;

export const getSqlForOutputColumns = (
    outputColumns: SqlColumn[],
    inputDatasets: InputDataset[],
    inputDatasetNames: string[],
    mode: Combine['setOperator']
) =>
    inputDatasets
        .map((dataset, i) =>
            getSelectStmtForUnionInputColumns(
                outputColumns,
                dataset.columns as InputColumnWithName[],
                inputDatasetNames[i]!
            )
        )
        .join(` ${mode} `);

export const selectAll = (dataset: string) => `SELECT * FROM ${quoteIfNeeded(dataset)}`;
